import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, switchMap } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { CurrentUserSelectors } from '@core/account-data';
import { FullCandidateSelectors } from '@core/candidate-data';
import { I18nService } from '@core/i18n-legacy';
import { candidateTaskScope, TaskErrorCode, TasksActions, tasksSelectors } from '@core/tasks-data';
import type { Task } from '@core/tasks-types';
import { TasksAnalytics } from '@core/tasks-util';
import { NotificationsController } from '@core/user-interface-legacy';

@Injectable()
export class CandidateProfileTasksDataService {
  private readonly actions$ = inject(Actions);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly tasksAnalytics = inject(TasksAnalytics);
  private readonly destroyRef = inject(DestroyRef);

  public taskLoading = new BehaviorSubject(false);
  public hasError = new BehaviorSubject(false);
  public currentAdmin = this.store.select(CurrentUserSelectors.data);

  public tasksByCandidateId = (candidateId: number) =>
    this.store.select(tasksSelectors.selectCandidateTasks(candidateId));
  public commonUsersByCandidateId = (candidateId: number) =>
    this.store.select(FullCandidateSelectors.commonUsersGrouped(candidateId));

  public submit(
    task: {
      title: string;
      dueDate: string | null;
      timezone: string | null;
      candidateId: number | null;
      adminIds: number[];
    },
    candidateId: number,
  ) {
    this.taskLoading.next(true);
    this.store.dispatch(
      TasksActions.createTask({
        payload: { data: task, scope: candidateTaskScope(candidateId) },
      }),
    );

    this.actions$
      .pipe(ofType(TasksActions.createTaskSuccess), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.tasksAnalytics.trackAddTask('single', !!task.dueDate, task.adminIds.length > 0, 1);
        this.notificationsCtrl.success(this.i18n.translate('app.tasks.add_success'));
        this.hasError.next(false);
        this.taskLoading.next(false);
      });

    this.actions$
      .pipe(ofType(TasksActions.createTaskFailed), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ payload }) => {
        const error = payload.error.errors[0];
        if (error.code === TaskErrorCode.CannotAssignAdminWithoutAccess) {
          this.notificationsCtrl.error(error.message);
        }
        this.hasError.next(true);
        this.taskLoading.next(false);
      });
  }

  public toggle({ task, completed }: { task: Task; completed: boolean }) {
    this.store.dispatch(
      TasksActions.updateTask({
        payload: { data: { id: task.id, completed } },
      }),
    );

    this.actions$
      .pipe(ofType(TasksActions.updateTaskSuccess), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const title = this.i18n.translate(
          completed ? 'app.dashboard.tasks.done.title' : 'app.dashboard.tasks.undone.title',
        );
        const message = this.i18n.translate(
          completed ? 'app.dashboard.tasks.done.message' : 'app.dashboard.tasks.undone.message',
        );

        const notificationRef = this.notificationsCtrl.success({
          title,
          message,
          topButtonLabel: this.i18n.translate('app.tasks.undo'),
        });

        if (completed) {
          this.tasksAnalytics.trackComplete(!!task.dueDate, task.adminIds?.length > 0);
        }

        notificationRef
          .topButtonClick()
          .pipe(
            take(1),
            tap(() => {
              this.store.dispatch(
                TasksActions.updateTask({
                  payload: { data: { id: task.id, completed: !completed } },
                }),
              );
            }),
            switchMap(() => this.actions$.pipe(ofType(TasksActions.updateTaskSuccess), take(1))),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => {
            notificationRef.close();
          });
      });
  }
}
